import { PlusOutlined, PoundCircleOutlined } from '@ant-design/icons';
import { Alert, App, Button, Card, Skeleton, Tooltip } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { btlApplicationFormApi } from '@api/btlApplicationForm/btlApplicationFormApi';
import { AffordabilityRunOutput, BtlAffordabilityRunResponse, BtlApplication } from '@api/btlApplicationForm/types';
import { FormValidationContext, useOnNext } from '@hooks/useBtlFormValidation';
import { useBtlApplicationInProgressById } from '@queries/btlApplications/useApplicationInProgressById/useBtlApplicationInProgressById';
import { useGetAffordabilityCheck } from '@queries/btlApplications/useAffordabilityCheck/useGetAffordabilityCheck';
import { useRunAffordabilityCheck } from '@queries/btlApplications/useAffordabilityCheck/useRunAffordabilityCheck';
import { getPageURLWithSection, PageURL } from '@router/pageURL';
import { TypedFormatMessageFn } from '@app-types/intl';
import { BtlApplicationFormPageEnum, BtlFormSectionParam } from '@pages/BtlApplicationFormPage/types';
import { useBtlFormMappedRoutes } from '@pages/BtlApplicationFormPage/useBtlFormMappedRoutes';
import { STATUS_TO_CAN_RUN_AFFORDABILITY } from '@pages/BtlApplicationFormPage/constants';
import { useBtlMissingFields } from '@queries/btlApplications/useBtlMissingFields';
export const AffordabilityCheck = ({ showTopSlicingEligibility }: { showTopSlicingEligibility?: boolean }) => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const { id = '' } = useParams();
  const { isLoading, data } = useGetAffordabilityCheck(id);
  const { isLoading: isLoadingApp, data: appData } = useBtlApplicationInProgressById(id);

  const affordabilityData = data?.[data?.length - 1 || 0]?.buyToLetOutput;

  const isAffordabilityPassed = affordabilityData?.isPassed;
  const isAffordabilityRunRequired = appData?.isAffordabilityRunRequired;

  // Refactored logic for result value and class
  let affordabilityItemValue: string | undefined;
  let affordabilityItemClassName = '';
  if (affordabilityData === undefined) {
    affordabilityItemValue = undefined;
    affordabilityItemClassName = '';
  } else if (isAffordabilityRunRequired) {
    affordabilityItemValue = 'Outdated';
    affordabilityItemClassName = 'text-[#D97706]';
  } else if (isAffordabilityPassed) {
    affordabilityItemValue = 'Passed';
    affordabilityItemClassName = 'text-green-600';
  } else {
    affordabilityItemValue = 'Failed';
    affordabilityItemClassName = 'text-red-500';
  }

  return (
    <Skeleton loading={isLoading || isLoadingApp} active>
      <div className={`flex flex-col gap-4 ${affordabilityData ? '' : 'opacity-50'}`}>
        <div className={'flex gap-12'}>
          <AffordabilityItem
            label={formatMessage({ id: 'btlApplicationForm.CASE_SUMMARY.affordability.result.label' })}
            value={affordabilityItemValue}
            valueClassName={affordabilityItemClassName}
            largeText={true}
          />
          <AffordabilityItem
            label={formatMessage({ id: 'btlApplicationForm.CASE_SUMMARY.affordability.ftv.label' })}
            value={
              affordabilityData?.applicationLtv === undefined
                ? '---'
                : `${((affordabilityData?.applicationLtv ?? 0) * 100).toFixed(0)}%`
            }
            largeText={true}
          />
        </div>
        <AffordabilityItem
          label={formatMessage({ id: 'btlApplicationForm.CASE_SUMMARY.affordability.maxFinance.label' })}
          value={
            affordabilityData?.maxFinancingPossible !== undefined
              ? new Intl.NumberFormat('en-GB', {
                  style: 'currency',
                  currency: 'GBP',
                  maximumFractionDigits: 0,
                }).format(affordabilityData?.maxFinancingPossible)
              : undefined
          }
        />
        <div className={'flex gap-8'}>
          <AffordabilityItem
            label={formatMessage({ id: 'btlApplicationForm.CASE_SUMMARY.affordability.monthlyPayment.label' })}
            value={
              affordabilityData?.monthlyPayment !== undefined
                ? new Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                    maximumFractionDigits: 0,
                  }).format(affordabilityData?.monthlyPayment)
                : undefined
            }
          />
          <AffordabilityItem
            label={formatMessage({ id: 'btlApplicationForm.CASE_SUMMARY.affordability.minRentalIncome.label' })}
            value={
              affordabilityData?.minRentalIncome !== undefined
                ? new Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                    maximumFractionDigits: 0,
                  }).format(affordabilityData?.minRentalIncome)
                : undefined
            }
          />
        </div>
        {/*<AffordabilityItem
          label={formatMessage({ id: 'btlApplicationForm.CASE_SUMMARY.affordability.rcr.label' })}
          value={`${((affordabilityData?.rcr ?? 0) * 100).toFixed(0)}%`}
        />*/}
        {showTopSlicingEligibility && (
          <AffordabilityItem
            label={formatMessage({ id: 'btlApplicationForm.CASE_SUMMARY.affordability.topSlicingEligible.label' })}
            value={
              affordabilityData?.isEligibleForTopSlicing !== undefined ? formatMessage({ id: 'common.yes' }) : '---'
            }
          />
        )}
      </div>
    </Skeleton>
  );
};

const AffordabilityItem = ({
  label,
  value,
  valueClassName,
  largeText,
}: {
  label: string;
  value: any;
  valueClassName?: string;
  largeText?: boolean;
}) => {
  return (
    <div>
      <div>{label}</div>
      <div className={`${largeText ? 'text-2xl font-bold' : 'text-lg font-bold'} ${valueClassName}`}>
        {value === undefined ? '---' : value}
      </div>
    </div>
  );
};

const AffordabilityAlert = ({
  appData,
  affordabilityData,
  formatMessage,
  togglingTopSlicing,
  toggleTopSlicing,
}: {
  appData: BtlApplication | undefined;
  affordabilityData: AffordabilityRunOutput | BtlAffordabilityRunResponse | undefined;
  formatMessage: TypedFormatMessageFn;
  togglingTopSlicing: boolean;
  toggleTopSlicing: () => void;
}) => {
  if (!affordabilityData || affordabilityData.isPassed || appData?.isAffordabilityRunRequired) return null;

  const { isTopSlicing } = appData || {};
  const { isEligibleForTopSlicing } = affordabilityData;

  const isTopSlicingEnabledAndEligible = isTopSlicing && isEligibleForTopSlicing;
  const canEnableTopSlicing = !isTopSlicing && isEligibleForTopSlicing && !appData?.isAffordabilityRunRequired;

  const messageId = isTopSlicingEnabledAndEligible
    ? 'btlApplicationForm.AFFORDABILITY_CHECK.topSlicingEnabled.label'
    : !isEligibleForTopSlicing
    ? 'btlApplicationForm.AFFORDABILITY_CHECK.topSlicingDisabled.label'
    : 'btlApplicationForm.AFFORDABILITY_CHECK.topSlicingEligible.label';

  const alertType = isEligibleForTopSlicing ? 'info' : 'warning';

  const description = canEnableTopSlicing ? (
    <div className='text-center'>
      <Button loading={togglingTopSlicing} className='mx-auto' onClick={toggleTopSlicing} icon={<PlusOutlined />}>
        {formatMessage({ id: 'btlApplicationForm.AFFORDABILITY_CHECK.addTopSlicing.button' })}
      </Button>
    </div>
  ) : !isTopSlicing ? (
    formatMessage({ id: 'btlApplicationForm.AFFORDABILITY_CHECK.topSlicingDisabled.message' })
  ) : null;

  return (
    <Alert
      className='mt-4'
      message={formatMessage({ id: messageId })}
      description={description}
      type={alertType}
      showIcon
    />
  );
};

const AffordabilityCheckForm = () => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const { refetch, isFetching, data } = useRunAffordabilityCheck(id);
  const { data: dataRuns } = useGetAffordabilityCheck(id);
  const { data: appData } = useBtlApplicationInProgressById(id);
  const { data: missingFieldsData } = useBtlMissingFields(id);
  const [togglingTopSlicing, setTogglingTopSlicing] = useState(false);

  const isMandatoryComplete = missingFieldsData?.isComplete;

  const affordabilityData = data ? data : dataRuns?.[dataRuns?.length - 1 || 0]?.buyToLetOutput;

  const formValidationContext = useContext(FormValidationContext);
  if (!formValidationContext) throw new Error('AffordabilityCheckForm must be used within a FormValidationProvider');
  const { setIsValid } = formValidationContext;

  useEffect(() => {
    const isFormValid = affordabilityData?.isPassed && !isFetching && isMandatoryComplete;
    setIsValid(!!isFormValid);
  }, [affordabilityData, isFetching]);

  const updateSectionInUrl = (section: BtlFormSectionParam) => {
    navigate({
      search: `?section=${section}`,
    });
  };

  const { nextSection } = useBtlFormMappedRoutes(appData);

  useOnNext(async () => {
    if (nextSection) {
      updateSectionInUrl(nextSection);
    }
  });

  const toggleTopSlicing = async () => {
    setTogglingTopSlicing(true);
    try {
      await btlApplicationFormApi.toggleTopSlicing(id, true);
      const nextSection = getPageURLWithSection(
        PageURL.BTL_APPLICATION_EDIT,
        { id },
        { section: BtlApplicationFormPageEnum.TOP_SLICING },
      );
      message.success('Top Slicing enabled');
      navigate(nextSection);
    } catch (e: any) {
      console.error(e);
      message.error(e.message);
    } finally {
      setTogglingTopSlicing(false);
    }
  };

  const canStatusRunAffordability =
    !appData?.status || STATUS_TO_CAN_RUN_AFFORDABILITY[appData.status as keyof typeof STATUS_TO_CAN_RUN_AFFORDABILITY];

  const hasProduct = !!appData?.product;

  return (
    <Card
      className={'w-fit'}
      loading={isFetching}
      actions={[
        <Tooltip
          key={'run'}
          title={!hasProduct ? formatMessage({ id: 'btlApplicationForm.AFFORDABILITY_CHECK.noProductSelected' }) : null}
          placement='top'
        >
          <span>
            <Button
              type={'link'}
              size={'large'}
              loading={isFetching}
              icon={<PoundCircleOutlined />}
              onClick={() => refetch()}
              disabled={!canStatusRunAffordability || !hasProduct}
            >
              Run Affordability Check
            </Button>
          </span>
        </Tooltip>,
      ]}
    >
      <>
        <AffordabilityCheck />
        <AffordabilityAlert
          appData={appData}
          affordabilityData={affordabilityData}
          formatMessage={formatMessage}
          togglingTopSlicing={togglingTopSlicing}
          toggleTopSlicing={toggleTopSlicing}
        />
      </>
    </Card>
  );
};

export default AffordabilityCheckForm;
